import React, {FC, useState, useContext} from "react"
import styled from "styled-components"
import {MenuItem} from "react-bootstrap"

import {FormattedMessage, useIntl} from "@irati/utils/lang"
import {Poll} from "@irati/types"
import DeletePollMutation from "@irati/recognition/apollo/mutation/Poll/Delete"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import tracking from "@irati-tracking/platform/platform"
import {UTMMedium, UTMSource, addUTMToLink} from "@irati-web/platform/src/utils/shared"

import MenuActionsBase, {MenuActionsProps} from "../../../shared/MenuActions"
import Wrapper from "../../shared/Poll/Wrapper"
import ConfirmDeletePoll from "../../shared/Poll/ConfirmDeletePoll"

interface Props extends Partial<MenuActionsProps> {
  poll: Poll
  onDeleteSuccess: () => void
}

const MenuActions = styled(MenuActionsBase)`
  display: flex;
  justify-content: flex-end;
`

const OpenPollActions: FC<Props> = (props) => {
  const [isDeleteMode, setIsDeleteMode] = useState(false)
  const {setDrawerOpen} = useContext(DrawerContext)
  const {formatMessage} = useIntl()
  const {poll, onDeleteSuccess} = props

  const enableDeleteMode = () => {
    setIsDeleteMode(true)
  }

  const disableDeleteMode = () => {
    setIsDeleteMode(false)
  }

  const sharePollLink = () => {
    const drawerData = {
      url: addUTMToLink(
        `${getDomain(authentication.business().subdomain)}/recognition/polls/poll/${poll.uuid}`,
        UTMSource.POLLS,
        UTMMedium.SHARE_LINK
      ),
      title: poll.title || "",
      alertText: "recognition.polls.openPoll.poll.shareDrawer.alert",
      source: "polls",
      type: "poll",
      entity_id: poll.uuid
    }
    if (navigator?.canShare?.(drawerData)) {
      setDrawerOpen(drawerData)
    } else {
      navigator.clipboard.writeText(drawerData.url).then(() => {
        tracking.share({source: "polls", type: "poll", entity_id: poll.uuid})
        triggerSuccessToast(formatMessage({id: "platform.shareToDrawer.toast"}))
      })
    }
  }

  return (
    <>
      <MenuActions pullRight item={poll} {...props} disabled={isDeleteMode}>
        {({isUserContent, canDeletePoll, isAdmin}) => [
          ((poll.active && !poll.archived) || isAdmin) && (
            <MenuItem key={1} onSelect={sharePollLink}>
              <FormattedMessage id="recognition.polls.openPoll.poll.btnShare.label" />
            </MenuItem>
          ),
          ((poll.active && !poll.archived && isUserContent && canDeletePoll) || isAdmin) && (
            <MenuItem key={1} onSelect={enableDeleteMode}>
              <FormattedMessage id="recognition.polls.openPoll.poll.btnDelete.label" />
            </MenuItem>
          )
        ]}
      </MenuActions>
      <Wrapper>
        {isDeleteMode && (
          <DeletePollMutation poll={poll} onCompleted={onDeleteSuccess}>
            {(mutate, apolloState) => (
              <ConfirmDeletePoll poll={poll} apolloState={apolloState} onCancel={disableDeleteMode} onConfirm={mutate} />
            )}
          </DeletePollMutation>
        )}
      </Wrapper>
    </>
  )
}

export default OpenPollActions
